import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Label from 'components/form/Label';
import Button from 'components/form/Button';
import TextArea from 'components/form/TextArea';
import TextInput from 'components/form/TextInput';
import FileUpload from 'components/form/FileUpload';
import MultipleSelect from 'components/form/MultipleSelect';
import ToggleContainer from 'components/Layout/ToggleContainer';
import {
  icons,
  participationMode,
  speakerCategoryOptions,
  limit,
} from 'utils/constants';
import {
  addAgenda,
  fetchDetailedViewData,
  getEvent,
  storeEventData,
  throwError,
  throwSuccess,
} from 'store/slices';
import {
  formatDate,
  getEventDate,
  getFilenameFromUrl,
  objectToFormData,
  titleCaseString,
} from 'utils/helpers';
import Dropdown from 'components/form/Dropdown';
import moment from 'moment';
import usePermission from 'hooks/usePermission';
import Loader from 'components/Layout/Loader';
import SessionTimeRange from 'components/form/SessionTimeRange/SessionTimeRange';

const AgendaForm = ({ eventId, eventTab, handleNext }) => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const path = window.location.pathname;
  const segments = path.split('/');
  const targetValue = segments[2] || '';

  const permissionMapping = {
    'event-management': 'Event Management',
    'chapters-groups': 'Chapters & Group Management',
  };
  const permissionName = permissionMapping[targetValue] || '';
  const { viewAccess, createAccess, updateAccess, deleteAccess } = usePermission(permissionName);


  const { eventData } = useSelector((state) => ({
    eventData: state.global.eventData,
  }));

  const [btnLoading, setBtnLoading] = useState('');
  const [initialValues, setInitialValues] = useState([]);
  const [toggleIndex, setToggleIndex] = useState(0);

  const isButtonHide = updateAccess === '1' || createAccess === '1';
  const hideDeleteBtn = deleteAccess === '1';

  const [recieverList, setRecieverList] = useState({
    list: [],
    name: '',
    total: 0,
    offset: 0,
    limit: limit,
    isLoading: true,
  });

  const [timer, setTimer] = useState('');

  const fetchEventDetails = async () => {
    await dispatch(getEvent(params ? params?.eventId : eventId));
  };

  const handelSave = async (elem, index) => {
    setBtnLoading(elem.date);
    const payloadData = {
      ...elem,
      agenda_sessions: JSON.stringify(elem.agenda_sessions),
      event_id: params ? params?.eventId : eventId,
    };
    const payload = objectToFormData(payloadData);
    const response = await dispatch(addAgenda(payload));
    if (response?.status === 200) {
      fetchEventDetails();
      dispatch(throwSuccess(response?.message));
      let newList = cloneDeep(initialValues);
      newList[index] = response.data;
      setInitialValues(newList);
    }
    setBtnLoading('');
  };

  // useEffect(() => {
  //   fetchEventDetails();
  //   return () => {
  //     dispatch(storeEventData({}));
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);


  useEffect(() => {
    fetchEventDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    const agendasList = [];
    if (eventData?.event_agendas) {
      eventData?.event_agendas.forEach((elem) => {
        const newAgenda = elem.agenda_sessions?.map((o) => {
          return {
            ...o,
            session_time: o?.session_time
              ? moment(o?.session_time, 'HH:mm:ss').format('HH:mm')
              : '',
          };
        });
        agendasList.push({
          ...elem,
          agenda_sessions:
            elem.agenda_sessions.length > 0
              ? newAgenda
              : [
                {
                  id: '',
                  session_time: '',
                  session_topic: '',
                  session_email_id: '',
                  session_room_name: '',
                  name: '',
                  description: '',
                  speaker_id: '',
                  speaker_category: '',
                  participation_mode: '',
                  transcription: '',
                  transcriptionFileName: '',
                },
              ],
        });
      });
    }
    setInitialValues(agendasList);
  }, [eventData]);

  // const validationSchema = Yup.array().of(
  //   Yup.object().shape({
  //     agenda_sessions: Yup.array().of(
  //       Yup.object().shape({
  //         session_time: Yup.string().required('Time is required.'),
  //         session_topic: Yup.string().required('Session topic is required.'),
  //         name: Yup.string().required('Name is required.'),
  //         description: Yup.string()
  //           .required('Description is required.')
  //           .max(100, 'Maximum 100 character allow for this field.'),
  //         speaker_id: Yup.string().required('Speaker is required.'),
  //         speaker_category: Yup.string().required(
  //           'Speaker category is required.'
  //         ),
  //         participation_mode: Yup.string().required(
  //           'Participation mode is required.'
  //         ),
  //         transcription: Yup.string().required(
  //           'Transcription file is required.'
  //         ),
  //       })
  //     ),
  //   })
  // );

  const validationSchema = Yup.array().of(
    Yup.object().shape({
      agenda_sessions: Yup.array().of(
        Yup.object().shape({
          session_email_id: Yup.string().when(['speaker_category'], {
            is: (speaker_category) => speaker_category == '9',
            then: (schema) => schema.required('Email Id is required.'),
            otherwise: (schema) => schema.notRequired(),
          }),
          session_time: Yup.string()
            .when(['speaker_category'], {
              is: (speaker_category) => speaker_category != '9',
              then: (schema) => schema.required('Session time is required.'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test(
              'is-overlap',
              'Session time overlaps with another session.',
              function (value) {
                const { session_end_time } = this.parent;
                // Skip overlap check if no values provided
                if (!value || !session_end_time) return true;

                // Access the array of sessions for the current day
                // this.from[1] is the array of sessions, this.path gives us the index
                const sessions = this.from[1].value.agenda_sessions;
                // Extract index from path like "[0].agenda_sessions[1].session_time"
                const match = this.path.match(/agenda_sessions\[(\d+)\]/);
                const currentIndex = match ? parseInt(match[1], 10) : -1;

                const currentStart = moment(value, 'HH:mm');
                const currentEnd = moment(session_end_time, 'HH:mm');

                if (!currentStart.isValid() || !currentEnd.isValid())
                  return true;

                const hasOverlap = sessions.some((session, index) => {
                  if (index === currentIndex) return false; // Skip self

                  const otherStart = moment(session.session_time, 'HH:mm');
                  const otherEnd = moment(
                    session.session_end_time,
                    'HH:mm'
                  );

                  if (!otherStart.isValid() || !otherEnd.isValid())
                    return false;

                  // Check for overlap: (StartA < EndB) && (EndA > StartB)
                  return (
                    currentStart.isBefore(otherEnd) &&
                    currentEnd.isAfter(otherStart)
                  );
                });

                return !hasOverlap;
              }
            ),

          session_end_time: Yup.string()
            .nullable()
            .when(['speaker_category', 'session_time'], {
              is: (speaker_category, session_time) =>
                speaker_category != '9' && !!session_time,
              then: (schema) => schema.required('Session end time is required.'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test(
              'is-after-start',
              'End time must be after start time.',
              function (value) {
                const { session_time } = this.parent;
                if (!session_time || !value) return true;
                const start = moment(session_time, 'HH:mm');
                const end = moment(value, 'HH:mm');
                return end.isAfter(start);
              }
            )
            .test(
              'is-overlap-end',
              'Session time overlaps with another session.',
              function (value) {
                const { session_time } = this.parent;
                // Skip overlap check if no values provided
                if (!session_time || !value) return true;

                const sessions = this.from[1].value.agenda_sessions;
                const match = this.path.match(/agenda_sessions\[(\d+)\]/);
                const currentIndex = match ? parseInt(match[1], 10) : -1;

                const currentStart = moment(session_time, 'HH:mm');
                const currentEnd = moment(value, 'HH:mm');

                if (!currentStart.isValid() || !currentEnd.isValid())
                  return true;

                const hasOverlap = sessions.some((session, index) => {
                  if (index === currentIndex) return false;

                  const otherStart = moment(
                    session.session_time,
                    'HH:mm'
                  );
                  const otherEnd = moment(
                    session.session_end_time,
                    'HH:mm'
                  );

                  if (!otherStart.isValid() || !otherEnd.isValid())
                    return false;

                  return (
                    currentStart.isBefore(otherEnd) &&
                    currentEnd.isAfter(otherStart)
                  );
                });

                return !hasOverlap;
              }
            ),
          session_topic: Yup.string().required('Session topic is required.'),
          name: Yup.string().when(['speaker_category'], {
            is: (speaker_category) => speaker_category != '9',
            then: (schema) => schema.required('Name is required.'),
            otherwise: (schema) => schema.notRequired(),
          }),
          description: Yup.string().when(['speaker_category'], {
            is: (speaker_category) => speaker_category != '9',
            then: (schema) => schema.required('Description is required.'),
            otherwise: (schema) => schema.notRequired(),
          }),
          speaker_id: Yup.string().when(['speaker_category'], {
            is: (speaker_category) => speaker_category == '9',
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required('Speaker is required.'),
          }),

          // speaker_id: Yup.string().required('Speaker is required.'),
          speaker_category: Yup.string().required(
            'Speaker category is required.'
          ),
          participation_mode: Yup.string().required(
            'Participation mode is required.'
          ),
          // transcription: Yup.string().when('speaker_category', {
          //   is: (val) => val !== '9',
          //   then: (schema) => schema.required('Transcription is required.'),
          //   otherwise: (schema) => schema.notRequired(),
          // }),
        })
      ),
    })
  );

  const getUserEmailList = async (data = recieverList, isReset) => {
    const { name, offset, limit } = data;
    const formData = objectToFormData({
      event_id: params.eventId,
      offset,
      limit,
      author_email: name,
    });

    const response = await dispatch(fetchDetailedViewData(formData));
    if (response.status === 200) {
      const newList =
        response?.data?.abstracts?.map((abstract) => ({
          email_id: abstract?.authorEmail || '',
          paper_title: abstract?.paperTitle || '',
          author_name: abstract?.authorName || '',
          participation_mode: abstract?.presentation_type || '',
          abstract_id: abstract?.id || ''
        })) || [];

      setRecieverList((prev) => {
        let resData = newList;
        let listData = isReset ? resData : [...prev.list, ...resData];
        return {
          ...prev,
          list: listData,
          total: response?.data?.result_count || 0,
          isLoading: false,
        };
      });
    }
  };

  const handleSearchUser = (e) => {
    let time = timer;
    clearTimeout(time);
    time = setTimeout(() => {
      let oldData = cloneDeep({
        ...recieverList,
        offset: 0,
        name: e?.toLowerCase(),
        isLoading: true,
      });
      setRecieverList(oldData);
      getUserEmailList(oldData, true);
    }, 800);
    setTimer(time);
  };

  const handelAgendaEmailScroll = () => {
    if (recieverList.list.length < recieverList.total) {
      let oldData = cloneDeep({
        ...recieverList,
        offset: recieverList.offset + limit,
        isLoading: true,
      });
      setRecieverList(oldData);
      getUserEmailList(oldData);
    }
  };

  useEffect(() => {
    getUserEmailList();
  }, []);

  return (
    <div className="">
      {/* {!eventTab && (
        <Card className="d-flex align-items-center unset-br cpe-26 cps-26 pt-2 pb-2">
          <div className="w-100 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <span
                className="d-flex"
                onClick={() => {
                  navigate(-1);
                }}
              >
                <img
                  src={icons.leftArrow}
                  alt="back"
                  className="h-21 me-3 pointer"
                />
              </span>
              <div>{`${params.agandaId === 'edit-agenda' ? 'Edit' : 'Add'
                } Agenda`}</div>
            </div>
            <div className="d-flex gap-3">
              <div>
                Event Date:{' '}
                {getEventDate(eventData?.start_date, eventData?.end_date)}
              </div>
            </div>
          </div>
        </Card>
      )} */}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {(props) => {
          const { values, errors, setFieldValue, handleChange } = props;
          return (
            <div className="fadeInUp">
              {values?.map((elem, index) => {
                return (
                  <div className="mt-3" key={index}>
                    <ToggleContainer
                      title={`Add Day ${index + 1} Sessions`}
                      onSubmit={() => { }}
                      defaultOpen={index === toggleIndex}
                      onClick={() => {
                        setToggleIndex(index);
                      }}
                      rightContent={
                        <div className="d-flex gap-3">
                          <div>
                            Event Date:{" "}
                            {getEventDate(eventData?.start_date, eventData?.end_date)}
                          </div>
                        </div>
                      }
                    >
                      <div className="row pt-3 g-2">
                        <div className="col-md-2 col-12 d-flex align-items-center cmb-22">
                          <Label label="Event Name" />
                        </div>
                        <div className="col-md-10 col-12 cmb-22">
                          <TextInput
                            placeholder="Event Name"
                            onChange={() => { }}
                            value={eventData?.event_name}
                            disabled
                          />
                        </div>
                        <div className="col-md-12">
                          {elem.agenda_sessions?.map(
                            (childElem, childIndex) => {
                              const lastElem =
                                elem.agenda_sessions.length - 1 === childIndex;
                              const isButton = (() => {
                                if (childElem?.speaker_category == '9') {
                                  return false;
                                }
                                return (
                                  !childElem?.session_time ||
                                  !childElem?.name ||
                                  !childElem?.description ||
                                  !childElem?.speaker_id
                                  /* !childElem?.transcription */
                                );
                              })();
                              return (
                                <div className="row mb-3 g-2" key={childIndex}>
                                  {elem.agenda_sessions.length > 1 &&
                                    hideDeleteBtn && (
                                      <div className="d-flex justify-content-end mb-2">
                                        <Button
                                          onClick={() => {
                                            const listArray = cloneDeep(
                                              elem.agenda_sessions
                                            );
                                            listArray.splice(childIndex, 1);
                                            setFieldValue([index], {
                                              ...elem,
                                              agenda_sessions: listArray,
                                            });
                                          }}
                                          btnStyle="delete-outline"
                                          icon={
                                            <i className="bi bi-trash me-2 d-flex align-items-center" />
                                          }
                                          text="Delete"
                                          className="cpt-5 cpb-5 cps-10 cpe-10"
                                          isSquare
                                        />
                                      </div>
                                    )}
                                  {childElem?.speaker_category == '9' && (
                                    <>
                                      <div className="col-md-2 d-flex align-items-center cmb-22">
                                        <Label label="Email ID" />
                                      </div>
                                      <div className="col-md-10 cmb-22">
                                        {/* <TextInput
                                          disabled={!isButtonHide}
                                          placeholder="Enter Email Id"
                                          id={`[${index}][agenda_sessions][${childIndex}][session_email_id]`}
                                          onChange={(e) => {
                                            const id = e.target.id;
                                            const value = e.target.value;

                                            setFieldValue(id, value);
                                          }}
                                          value={childElem.session_email_id}
                                          error={
                                            errors?.[index]?.agenda_sessions?.[
                                              childIndex
                                            ]?.session_email_id
                                          }
                                        /> */}

                                        <Dropdown
                                          isClearable
                                          disabled={!isButtonHide}
                                          id={`[${index}][agenda_sessions][${childIndex}][session_email_id]`}
                                          options={recieverList?.list}
                                          optionKey="email_id"
                                          optionValue="email_id"
                                          onMenuScrollToBottom={() =>
                                            handelAgendaEmailScroll()
                                          }
                                          onInputChange={handleSearchUser}
                                          onChange={(e) => {
                                            const selectedEmail =
                                              e.target.value;
                                            handleChange(e); // update email value in formik

                                            const relatedData =
                                              recieverList?.list?.find(
                                                (item) =>
                                                  item?.email_id ==
                                                  selectedEmail
                                              );

                                            if (relatedData) {
                                              setFieldValue(
                                                `[${index}][agenda_sessions][${childIndex}][session_topic]`,
                                                relatedData.paper_title || ''
                                              );
                                              setFieldValue(
                                                `[${index}][agenda_sessions][${childIndex}][name]`,
                                                childElem?.speaker_category ==
                                                  '9'
                                                  ? relatedData.author_name
                                                  : relatedData.paper_title
                                              );
                                              setFieldValue(
                                                `[${index}][agenda_sessions][${childIndex}][abstract_id]`,
                                                relatedData?.abstract_id || ''
                                              );
                                              setFieldValue(
                                                `[${index}][agenda_sessions][${childIndex}][participation_mode]`,
                                                relatedData?.participation_mode
                                                  ? relatedData?.participation_mode ===
                                                    'Physical'
                                                    ? '1'
                                                    : '2'
                                                  : ''
                                              );
                                            } else {
                                              setFieldValue(
                                                `[${index}][agenda_sessions][${childIndex}][session_topic]`,
                                                ''
                                              );
                                              setFieldValue(
                                                `[${index}][agenda_sessions][${childIndex}][name]`,
                                                ''
                                              );
                                              setFieldValue(
                                                `[${index}][agenda_sessions][${childIndex}][abstract_id]`,
                                                ''
                                              );
                                              setFieldValue(
                                                `[${index}][agenda_sessions][${childIndex}][participation_mode]`,
                                                ''
                                              );
                                            }
                                          }}
                                          placeholder="Select Email Id"
                                          value={childElem?.session_email_id}
                                          isLoading={recieverList?.isLoading}
                                          error={
                                            errors?.[index]?.agenda_sessions?.[
                                              childIndex
                                            ]?.session_email_id
                                          }
                                        />
                                      </div>
                                    </>
                                  )}

                                  {(() => {
                                    const dayLabel = (
                                      <div className="col-md-2 col-12 d-flex align-items-center cmb-22">
                                        <Label label={`Day ${index + 1}`} />
                                      </div>
                                    );

                                    const dateField = (
                                      <div className={`${eventTab ? "col-md-6" : "col-md-2"} col-12 cmb-22`}>
                                        <TextInput
                                          placeholder="Select Date"
                                          onChange={() => { }}
                                          value={formatDate(
                                            elem?.date,
                                            'DD MMM YYYY'
                                          )}
                                          disabled
                                        />
                                      </div>
                                    );

                                    const roomField = (
                                      <div className={`${eventTab ? "col-md-6" : "col-md-1"} col-12 cmb-22`}>
                                        <TextInput
                                          placeholder="Enter Room Name"
                                          id={`[${index}][agenda_sessions][${childIndex}][session_room_name]`}
                                          onChange={(e) => {
                                            const id = e.target.id;
                                            const value = titleCaseString(
                                              e.target.value
                                            );
                                            setFieldValue(id, value);
                                          }}
                                          value={childElem.session_room_name}
                                        />
                                      </div>
                                    );

                                    const sessionTimeRange = (
                                      <div className={`${eventTab ? "col-md-12" : "col-md-7"} col-12 cmb-22`}>
                                        <SessionTimeRange
                                          isAgenda={true}
                                          startLabel="Session Start"
                                          endLabel="Session End"
                                          startId={`[${index}][agenda_sessions][${childIndex}][session_time]`}
                                          endId={`[${index}][agenda_sessions][${childIndex}][session_end_time]`}

                                          startValue={childElem?.session_time}
                                          endValue={childElem?.session_end_time}
                                          disabledStart={!isButtonHide}
                                          disabledEnd={!isButtonHide}
                                          startError={errors?.[index]?.agenda_sessions?.[childIndex]?.session_time}
                                          endError={errors?.[index]?.agenda_sessions?.[childIndex]?.session_end_time}
                                          onDurationChange={(value) =>
                                            setFieldValue(
                                              `[${index}][agenda_sessions][${childIndex}][duration]`,
                                              value
                                            )
                                          }
                                          onStartChange={(value) => {
                                            const id = `[${index}][agenda_sessions][${childIndex}][session_time]`;
                                            if (!value) {
                                              setFieldValue(id, '');
                                              return;
                                            }
                                            // const isDuplicate = elem?.agenda_sessions?.some(
                                            //   (o, sessionIdx) => sessionIdx !== childIndex && o?.session_time === value
                                            // );
                                            // if (isDuplicate) {
                                            //   dispatch(
                                            //     throwError({
                                            //       message: `${moment(value, 'HH:mm').format('hh:mm A')} is already taken.`,
                                            //     })
                                            //   );
                                            //   return;
                                            // }
                                            setFieldValue(id, value);
                                          }}
                                          onEndChange={(value) => {
                                            setFieldValue(
                                              `[${index}][agenda_sessions][${childIndex}][session_end_time]`,
                                              value || ''
                                            );
                                          }}
                                        />
                                      </div>
                                    );

                                    if (eventTab) {
                                      return (
                                        <>
                                          {dayLabel}
                                          <div className="col-md-10">
                                            <div className="row">
                                              {dateField}
                                              {roomField}
                                              {sessionTimeRange}
                                            </div>
                                          </div>
                                        </>
                                      );
                                    }

                                    return (
                                      <>
                                        {dayLabel}
                                        {dateField}
                                        {roomField}
                                        {sessionTimeRange}
                                      </>
                                    );
                                  })()}


                                  {/* new field */}
                                  <div className="col-md-2 d-flex align-items-center cmb-22">
                                    <Label label="Session Topic" />
                                  </div>
                                  <div className="col-md-10 cmb-22">
                                    <TextInput
                                      disabled={!isButtonHide}
                                      placeholder="Topic Name"
                                      id={`[${index}][agenda_sessions][${childIndex}][session_topic]`}
                                      onChange={(e) => {
                                        const id = e.target.id;
                                        const value = titleCaseString(
                                          e.target.value
                                        );
                                        setFieldValue(id, value);
                                      }}
                                      value={childElem.session_topic}
                                      error={
                                        errors?.[index]?.agenda_sessions?.[
                                          childIndex
                                        ]?.session_topic
                                      }
                                    />
                                  </div>

                                  <div className="col-md-2 d-flex align-items-center cmb-22">
                                    <Label
                                      label={
                                        childElem?.speaker_category == '9'
                                          ? 'Author Name'
                                          : 'Session Name'
                                      }
                                    />
                                  </div>
                                  <div className="col-md-10 cmb-22">
                                    <TextInput
                                      disabled={!isButtonHide}
                                      placeholder="Name"
                                      id={`[${index}][agenda_sessions][${childIndex}][name]`}
                                      onChange={(e) => {
                                        const id = e.target.id;
                                        const value = titleCaseString(
                                          e.target.value
                                        );
                                        setFieldValue(id, value);
                                      }}
                                      value={childElem.name}
                                      error={
                                        errors?.[index]?.agenda_sessions?.[
                                          childIndex
                                        ]?.name
                                      }
                                    />
                                  </div>
                                  <div className="col-md-2 d-flex align-items-center cmb-22">
                                    <Label label="Description" />
                                  </div>
                                  <div className="col-md-10 cmb-22">
                                    <TextArea
                                      disabled={!isButtonHide}
                                      placeholder="Description"
                                      rows={3}
                                      id={`[${index}][agenda_sessions][${childIndex}][description]`}
                                      onChange={(e) => {
                                        const id = e.target.id;
                                        const value = titleCaseString(
                                          e.target.value
                                        );
                                        setFieldValue(id, value);
                                      }}
                                      value={childElem.description}
                                      error={
                                        errors?.[index]?.agenda_sessions?.[
                                          childIndex
                                        ]?.description
                                      }
                                    />
                                  </div>
                                  {/* <div className="col-md-2 d-flex align-items-center cmb-22">
                                    <Label label="Speaker" />
                                  </div>
                                  <div className="col-md-10 cmb-22">
                                    <MultipleSelect
                                      disabled={!isButtonHide}
                                      placeholder="Select Speakers"
                                      id={`[${index}][agenda_sessions][${childIndex}][speaker_id]`}
                                      value={childElem.speaker_id}
                                      options={
                                        eventData?.speaker_details?.map(
                                          (elm) => {
                                            return {
                                              ...elm,
                                              dName:
                                                elm?.name || elm?.new_user_name,
                                            };
                                          }
                                        ) || []
                                      }
                                      optionKey="id"
                                      optionValue="dName"
                                      onChange={handleChange}
                                      error={
                                        errors?.[index]?.agenda_sessions?.[
                                          childIndex
                                        ]?.speaker_id
                                      }
                                    />
                                  </div> */}

                                  {/* new fields */}
                                  <div className="col-md-2 d-flex align-items-center cmb-22">
                                    <Label label="Speaker Categories" />
                                  </div>
                                  <div className="col-md-10 cmb-22">
                                    <Dropdown
                                      disabled={!isButtonHide}
                                      id={`[${index}][agenda_sessions][${childIndex}][speaker_category]`}
                                      options={speakerCategoryOptions}
                                      optionKey="id"
                                      optionValue="name"
                                      onChange={handleChange}
                                      placeholder="Select Category"
                                      value={childElem.speaker_category}
                                      error={
                                        errors?.[index]?.agenda_sessions?.[
                                          childIndex
                                        ]?.speaker_category
                                      }
                                    />
                                  </div>

                                  {(() => {
                                    const selectedCategory =
                                      speakerCategoryOptions.find(
                                        (item) =>
                                          item.id === childElem.speaker_category
                                      );

                                    if (!selectedCategory) return null;

                                    const speakerOptions =
                                      eventData?.speaker_details?.map(
                                        (elm) => ({
                                          ...elm,
                                          dName:
                                            elm?.name || elm?.new_user_name,
                                        })
                                      ) || [];

                                    return (
                                      <>
                                        <div className="col-md-2 d-flex align-items-center cmb-22">
                                          <Label label="Speaker" />
                                        </div>
                                        <div className="col-md-10 cmb-22">
                                          {selectedCategory.type ===
                                            'Multiple' ? (
                                            <MultipleSelect
                                              disabled={!isButtonHide}
                                              placeholder="Select Speakers"
                                              id={`[${index}][agenda_sessions][${childIndex}][speaker_id]`}
                                              value={childElem.speaker_id}
                                              options={speakerOptions}
                                              optionKey="id"
                                              optionValue="dName"
                                              onChange={handleChange}
                                              error={
                                                errors?.[index]
                                                  ?.agenda_sessions?.[
                                                  childIndex
                                                ]?.speaker_id
                                              }
                                            />
                                          ) : (
                                            <Dropdown
                                              disabled={!isButtonHide}
                                              placeholder="Select Speaker"
                                              id={`[${index}][agenda_sessions][${childIndex}][speaker_id]`}
                                              value={childElem.speaker_id}
                                              options={speakerOptions}
                                              optionKey="id"
                                              optionValue="dName"
                                              onChange={handleChange}
                                              error={
                                                errors?.[index]
                                                  ?.agenda_sessions?.[
                                                  childIndex
                                                ]?.speaker_id
                                              }
                                            />
                                          )}
                                        </div>
                                      </>
                                    );
                                  })()}

                                  <div className="col-md-2 d-flex align-items-center cmb-22">
                                    <Label label="Participation Mode" />
                                  </div>
                                  <div className="col-md-10 cmb-22">
                                    <Dropdown
                                      disabled={!isButtonHide}
                                      id={`[${index}][agenda_sessions][${childIndex}][participation_mode]`}
                                      options={participationMode}
                                      optionKey="id"
                                      optionValue="name"
                                      onChange={handleChange}
                                      placeholder="Select Mode"
                                      value={childElem.participation_mode}
                                      error={
                                        errors?.[index]?.agenda_sessions?.[
                                          childIndex
                                        ]?.participation_mode
                                      }
                                    />
                                  </div>

                                  <div className="col-md-2 d-flex align-items-center cmb-22">
                                    <Label label="Transcription" />
                                  </div>
                                  <div className="col-md-10 cmb-22">
                                    <FileUpload
                                      disabled={!isButtonHide}
                                      id={`[${index}][agenda_sessions][${childIndex}][transcription]`}
                                      onChange={(e) => {
                                        const id = e.target.id;
                                        const value = e.target.value;
                                        const fileName = e.target.fileName;
                                        setFieldValue(
                                          `[${index}][agenda_sessions][${childIndex}][transcriptionFileName]`,
                                          fileName
                                        );
                                        setFieldValue(id, value);
                                      }}
                                      fileText={getFilenameFromUrl(
                                        childElem?.transcriptionFileName ||
                                        childElem.transcription
                                      )}
                                      error={
                                        errors?.[index]?.agenda_sessions?.[
                                          childIndex
                                        ]?.transcription
                                      }
                                    />
                                  </div>
                                  {lastElem && isButtonHide && (
                                    <div className="d-flex">
                                      <Button
                                        onClick={() => {
                                          let newArray =
                                            JSON.parse(
                                              JSON.stringify(
                                                elem?.agenda_sessions
                                              )
                                            ) || [];
                                          newArray.push({
                                            id: '',
                                            session_time: '',
                                            name: '',
                                            description: '',
                                            speaker_id: '',
                                            transcription: '',
                                          });
                                          setFieldValue([index], {
                                            ...elem,
                                            agenda_sessions: newArray,
                                          });
                                        }}
                                        text="+ Add Next Session"
                                        btnStyle="primary-light"
                                        className="text-14-500 cps-20 cpe-20"
                                        disabled={isButton}
                                        isSquare
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            }
                          )}
                        </div>

                        <div className="d-flex justify-content-center gap-4 cmt-30 cpb-20">
                          <Button
                            text="Cancel"
                            isRounded
                            btnStyle="light-outline"
                            className="cps-40 cpe-40"
                            onClick={() => { }}
                            disabled={btnLoading === `${elem?.date}`}
                          />
                          {isButtonHide && (
                            <Button
                              isRounded
                              text="Done"
                              btnStyle="primary-dark"
                              className="cps-40 cpe-40"
                              btnLoading={btnLoading === `${elem?.date}`}
                              disabled={
                                JSON.stringify(elem) ===
                                JSON.stringify(initialValues[index]) ||
                                Object.keys(
                                  errors?.[index]?.agenda_sessions || {}
                                )?.length > 0 || btnLoading === `${elem?.date}`
                              }
                              onClick={() => {
                                handelSave(elem, index);
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </ToggleContainer>
                  </div>
                );
              })}
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default AgendaForm;
